import React, { useContext, useRef, useState } from "react";
import "./styles.scss";
import { RouteComponentProps } from "react-router-dom";
import {
  ActionButton,
  BusinessLoyaltyProgramSection,
  type BusinessLoyaltyProgramSectionHandle,
  ContactInfoForm,
  ContactInfoStep,
  ContactInfoWorkflow,
  IContactInfo,
} from "halifax";
import { ITravelerStepErrors, SELECTED_PACKAGE_FLIGHT_PAX } from "redmond";
import {
  ContactSelectors,
  FlightPassengerSelectors,
  GenericChildState,
  getChildState,
  getParentState,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector,
  useChildMachineSelector,
  useChildMachineState,
} from "@capone/checkout";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import { PackagesContactInfoConnectorProps } from "./container";
import { Event, TEvent } from "../../state/events";
import { PackagesMachineContext } from "../../state/types";
import {
  CONTACT_INFO_NOTICE,
  CONTACT_INFO_SUBTITLE,
  CONTACT_INFO_TITLE,
  CONTACT_INFO_TITLE_MOBILE,
  CONTACT_INFO_TITLE_MOBILE_V1,
  CONTINUE_TEXT,
  SAVING_TEXT,
} from "./textConstants";
import { PackagesPriceBreakdown } from "../PriceBreakdown";
import { DesktopPackageBookValidationError } from "../DesktopPackagesBookWorkflow";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";
import { MobileBookingSteps } from "../MobileBookingSteps";
import { trackEvent } from "../../../../api/v0/trackEvent";
import { useIsTravelApp, isVesperTenant, getConfigTenant } from "@capone/common";
import { ClientContext } from "../../../../App";

export interface PackagesContactInfoProps
  extends PackagesContactInfoConnectorProps,
    RouteComponentProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopPackageBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopPackageBookValidationError[]
  ) => void;
  contactFormOnly?: boolean;
}

export const PackagesContactInfo = ({
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,
  contactFormOnly,
  tripDetails,
}: PackagesContactInfoProps) => {
  const expState = useExperiments();

  const { isTravelApp } = useIsTravelApp();

  const { sessionInfo } = useContext(ClientContext);

  const businessLoyaltyRef =
    useRef<BusinessLoyaltyProgramSectionHandle>(null);

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const isVesper = isVesperTenant(getConfigTenant());

  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    phone: false,
    email: false,
    travelerSelect: false,
  });

  const [state, send] = useCheckoutState<TEvent, PackagesMachineContext>();

  const [, passengerChildMachineSend] = useChildMachineState<
    TEvent,
    PackagesMachineContext
  >(ParentState.passengerInformation);

  const parentState = getParentState(state.value) as ParentState;
  const childState = getChildState(state.value);

  const contactInfo = useCheckoutStateSelector(
    ContactSelectors.getContactInformation
  );

  const contactInfoLoading = useCheckoutStateSelector(
    ContactSelectors.getContactInformationLoading
  );

  const passengersValidated = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getPassengerStateValidated
  );

  const contactInfoValidated = useCheckoutStateSelector(
    ContactSelectors.getContactInformationValidated
  );

  const canContinue = isMobile || (passengersValidated && contactInfoValidated);

  const handleContinue = async () => {
    if (canContinue) {
      const persistOk =
        (await businessLoyaltyRef.current?.persistIfNeeded()) ?? true;
      if (!persistOk) return;
      setValidationErrorTypes?.([]);
      /*
        desktop is on flight passengers step here, which expects the "next" event
        to be sent to the passengers child machine
      */
      if (isMobile) {
        send(Event.NEXT);
      } else {
        passengerChildMachineSend(Event.NEXT);
        trackEvent({
          eventName: SELECTED_PACKAGE_FLIGHT_PAX,
          properties: {},
          encryptedProperties: [],
        });
      }
    } else if (!passengersValidated && !contactInfoValidated) {
      setValidationErrorTypes?.(["flight-travelers", "contact-info"]);
    } else if (!passengersValidated) {
      setValidationErrorTypes?.(["flight-travelers"]);
    } else if (!contactInfoValidated) {
      setValidationErrorTypes?.(["contact-info"]);
    }
  };

  const handleBack = () => {
    send(Event.PREVIOUS);
  };

  const onContactInfoChange = (newContactInfo: IContactInfo) => {
    send({ type: Event.CHANGE, contactInfo: newContactInfo });
  };

  const goToContactInfo = () => [
    send({ type: Event.GO_TO_CONTACT_INFORMATION }),
  ];

  React.useEffect(() => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
    }
  }, [canContinue]);

  React.useEffect(() => {
    if (passengersValidated) {
      setValidationErrorTypes?.(
        validationErrorTypes?.filter((error) => error !== "flight-travelers") ||
          []
      );
    }
  }, [passengersValidated]);

  React.useEffect(() => {
    if (contactInfoValidated) {
      setValidationErrorTypes?.(
        validationErrorTypes?.filter((error) => error !== "contact-info") || []
      );
    }
  }, [contactInfoValidated]);

  if (isMobile)
    return (
      <ContactInfoWorkflow
        titles={{
          contactInfoTitle: CONTACT_INFO_TITLE_MOBILE,
          contactInfoSubtitle: CONTACT_INFO_SUBTITLE,
          buttonTitle: CONTINUE_TEXT,
        }}
        progressStep={
          parentState === ParentState.review
            ? ContactInfoStep.Main
            : ContactInfoStep.ContactInfoForm
        }
        setProgressStep={(step) => {
          if (parentState === ParentState.review) {
            switch (step) {
              case ContactInfoStep.ContactInfoForm:
                goToContactInfo();
                break;
              case ContactInfoStep.Main:
              default:
                break;
            }
          }
        }}
        contactInfo={contactInfo}
        onContactInfoChange={onContactInfoChange}
        isMobile
        onContinueClick={handleContinue}
        onGoBack={handleBack}
        className={clsx("packages-contact-info-workflow-root", {
          "cx-optimizations-v1": isPackageCheckoutOptimizationExperiment,
          redesigned: isVesper,
        })}
        mobileHeaderElement={
          isPackageCheckoutOptimizationExperiment ? (
            <Typography className="header-text">
              {CONTACT_INFO_TITLE_MOBILE_V1}
            </Typography>
          ) : (
            <PackagesPriceBreakdown isMobile dropdown />
          )
        }
        customHeader={
          <Box className="packages-book-mobile-contact-info-header">
            {isPackageCheckoutOptimizationExperiment ? undefined : (
              <Typography
                className="mobile-contact-info-header-title"
                variant="h3"
              >
                {CONTACT_INFO_TITLE_MOBILE}
              </Typography>
            )}

            {isPackageCheckoutOptimizationExperiment ? (
              <>
                <MobileBookingSteps currentStep={3} />
                <hr className="heading-rule" />
              </>
            ) : undefined}

            <Typography className="mobile-contact-info-header-subtitle">
              {CONTACT_INFO_SUBTITLE}
            </Typography>
            {isPackageCheckoutOptimizationExperiment ? undefined : (
              <hr className="heading-rule" />
            )}
          </Box>
        }
        showMobilePopoverTransition={false}
        contactFormOnly={contactFormOnly}
        formExtraContent={
          tripDetails ? (
            <BusinessLoyaltyProgramSection
              ref={businessLoyaltyRef}
              tripDetails={tripDetails}
              sessionInfo={sessionInfo}
              isMobile
              trackEvent={trackEvent}
            />
          ) : undefined
        }
        bottomContent={
          isPackageCheckoutOptimizationExperiment ? (
            <PackagesPriceBreakdown isMobile popover />
          ) : undefined
        }
        hideBackButton={isTravelApp}
      />
    );

  return (
    <>
      <ContactInfoForm
        disabled={
          ![
            ParentState.contactInformation,
            ParentState.passengerInformation,
          ].includes(parentState)
        }
        title={CONTACT_INFO_TITLE}
        subtitle={CONTACT_INFO_SUBTITLE}
        contactInfo={contactInfo}
        onContactInfoChange={onContactInfoChange}
        showErrors={showErrors}
        setShowErrors={setShowErrors}
        loading={contactInfoLoading}
        className={clsx("packages-contact-info-form", {
          error: validationErrorTypes?.includes("contact-info"),
        })}
      />
      <Typography
        className={clsx("packages-contact-info-notice", {
          error: validationErrorTypes?.some((error) =>
            ["contact-info", "flight-travelers"].includes(error)
          ),
        })}
      >
        {CONTACT_INFO_NOTICE}
      </Typography>
      {tripDetails && (
        <BusinessLoyaltyProgramSection
          ref={businessLoyaltyRef}
          tripDetails={tripDetails}
          sessionInfo={sessionInfo}
          disabled={
            ![
              ParentState.contactInformation,
              ParentState.passengerInformation,
            ].includes(parentState)
          }
          trackEvent={trackEvent}
        />
      )}
      <ActionButton
        onClick={handleContinue}
        message={
          childState === GenericChildState.saving ? SAVING_TEXT : CONTINUE_TEXT
        }
        className="packages-continue-button"
        disabled={childState === GenericChildState.saving}
      />
    </>
  );
};
